/**
 * The mobile app's palette (Xale_App `src/design/tokens.ts` + `brand.ts`),
 * kept as plain values so a class like `bg-[#EDF2EF]` and a style like
 * `{ color: M.textMuted }` name the same colour.
 */
export const M = {
  bg: "#F4F7F5",
  surface: "#FFFFFF",
  surfaceAlt: "#EDF2EF",
  border: "#E6E8E7",
  hairline: "#EFF2F0",
  text: "#051912",
  textMuted: "#697571",
  textFaint: "#9BA3A0",
  accent: "#218760",
  accentSoft: "#EAF5F1",
  greenDeep: "#175C42",
  danger: "#D0453A",
  warning: "#C2801C",
  info: "#2F6FD0",
  whatsapp: "#25D366",
} as const;

/** Stage tints by pipeline order, cycling (the app's `usePipelineStages`). */
export const STAGE_TINTS = [
  "#218760", "#4C7FB8", "#8570BE", "#B58540", "#C77E4A", "#3F8C8C", "#6E8CA8", "#B26689", "#729446",
];
export const stageTint = (order: number | null | undefined) =>
  order == null ? "#697571" : STAGE_TINTS[((order % STAGE_TINTS.length) + STAGE_TINTS.length) % STAGE_TINTS.length];

/** Follow-up bucket dots (rail pills, chips). */
export const FU_DOT = { today: "#218760", overdue: "#D11F1F", tomorrow: "#E0A100", later: "#EA9A3E" } as const;

/** Status kinds: text / background / border, the app's KIND_STYLE. */
export const KIND_STYLE = {
  qualify: { fg: "#7A1FA8", bg: "#F5EBFF", border: "#E4CCF7", label: "Qualify" },
  rejection: { fg: "#A61B34", bg: "#FEECEF", border: "#F8CBD3", label: "Rejected" },
  rollback: { fg: "#B4530A", bg: "#FFF4E6", border: "#FBDDB8", label: "Rollback" },
} as const;

/** Filled chip colour per status kind on the leads strip. */
export const STATUS_KIND_TINT = { rejection: "#D0453A", rollback: "#D98324", qualify: "#7A4FD0", normal: "#175C42" } as const;

const AVATAR_COLORS = ["#218760", "#4C7FB8", "#B58540", "#8570BE", "#3F8C8C", "#B26689"];

export function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export function initialsOf(name: string | null | undefined): string {
  return (name ?? "")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/** The app's type: Poppins. Loaded only for the phone shell, once. */
export function ensureMobileFont(): void {
  if (typeof document === "undefined" || document.getElementById("xm-font")) return;
  const pre = document.createElement("link");
  pre.rel = "preconnect";
  pre.href = "https://fonts.gstatic.com";
  pre.crossOrigin = "anonymous";
  const link = document.createElement("link");
  link.id = "xm-font";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap";
  document.head.append(pre, link);
}

/** en-IN number. */
export const fmtN = (n: number) => Math.round(n).toLocaleString("en-IN");
/** "1.1k" / "2.3L" for a pill count. */
export function fmtCompact(n: number): string {
  if (n >= 1e5) return `${(n / 1e5).toFixed(n >= 1e6 ? 0 : 1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}
